import React from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Category } from '../types';
import { ChevronRight } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

interface CategoryHeaderProps {
  category: Category;
  subcategory?: Category;
}

export default function CategoryHeader({ category, subcategory }: CategoryHeaderProps) {
  const { theme } = useTheme();
  const CategoryIcon = Icons[category.icon as keyof typeof Icons];
  const SubcategoryIcon = subcategory ? Icons[subcategory.icon as keyof typeof Icons] : null;

  return (
    <div className="mb-8">
      <div className="flex items-center space-x-2 text-sm mb-4">
        <Link to="/" className={`hover:text-blue-600 ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
        }`}>
          Home
        </Link>
        <ChevronRight className={`h-4 w-4 ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
        }`} />
        <Link to={`/category/${category.slug}`} className={`hover:text-blue-600 ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
        }`}>
          {category.name}
        </Link>
        {subcategory && (
          <>
            <ChevronRight className={`h-4 w-4 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <span className={theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}>
              {subcategory.name}
            </span>
          </>
        )}
      </div>

      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            {CategoryIcon && <CategoryIcon className="h-8 w-8 text-blue-600" />}
            <h1 className={`text-3xl font-bold ${
              theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
            }`}>
              {subcategory ? subcategory.name : category.name}
            </h1>
          </div>
          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
            {subcategory ? subcategory.description : category.description}
          </p>
        </div>

        {!subcategory && category.subcategories && (
          <div className={`
            hidden lg:block ml-8 rounded-lg shadow-md p-4 min-w-[250px] transition-colors duration-200
            ${theme === 'dark'
              ? 'bg-gray-800 shadow-gray-900/20'
              : 'bg-white shadow-gray-200/50'
            }
          `}>
            <h3 className={`font-medium mb-3 ${
              theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
            }`}>
              Subcategories
            </h3>
            <div className="space-y-2">
              {category.subcategories.map(sub => {
                const SubIcon = Icons[sub.icon as keyof typeof Icons];
                return (
                  <Link
                    key={sub.id}
                    to={`/category/${category.slug}/${sub.slug}`}
                    className={`
                      flex items-center space-x-2 hover:text-blue-600 transition-colors
                      ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}
                    `}
                  >
                    {SubIcon && <SubIcon className="h-4 w-4" />}
                    <span>{sub.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}